import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { POSTS, CATEGORIES } from "../data/blog";
import Reveal from "../components/Reveal";
import { useSeo, breadcrumbLd } from "../lib/seo";

export default function Blog() {
  const [cat, setCat] = useState("All");
  const posts = cat === "All" ? POSTS : POSTS.filter((p) => p.category === cat);
  const pillar = POSTS.find((p) => p.pillar);

  useSeo({
    title: "Blog — Looksmaxxing, skincare & facial fitness guides",
    description: "Evidence-based guides on looksmaxxing, skincare, jawline training, facial symmetry and AI face analysis. Real advice, no hype.",
    path: "/blog",
    jsonLd: breadcrumbLd([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }]),
  });

  return (
    <div className="container py-16">
      <Reveal>
        <span className="label-eyebrow">Blog</span>
        <h1 className="mt-3 font-display text-5xl sm:text-6xl">The looksmaxxing library.</h1>
        <p className="mt-4 max-w-xl text-muted-foreground">Evidence-based guides on what actually improves your face — and how to measure it.</p>
      </Reveal>

      {/* Pillar feature */}
      {pillar && (
        <Reveal>
          <Link to={`/blog/${pillar.slug}`} className="mt-10 block overflow-hidden rounded-2xl border border-primary/30 bg-card p-8 transition hover:border-primary/60" data-testid="pillar-post">
            <span className="rounded-full bg-primary/15 px-3 py-1 text-xs font-semibold text-primary">Pillar guide</span>
            <h2 className="mt-4 max-w-2xl font-display text-3xl sm:text-4xl">{pillar.title}</h2>
            <p className="mt-3 max-w-2xl text-muted-foreground">{pillar.excerpt}</p>
            <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">Read the guide <ArrowRight className="h-4 w-4" /></span>
          </Link>
        </Reveal>
      )}

      {/* Categories */}
      <div className="mt-10 flex flex-wrap gap-2">
        {["All", ...CATEGORIES].map((c) => (
          <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c}`} className={`rounded-full border px-4 py-1.5 text-sm transition ${cat === c ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {c}
          </button>
        ))}
      </div>

      <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {posts.filter((p) => !p.pillar || cat !== "All").map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.04}>
            <Link to={`/blog/${p.slug}`} className="flex h-full flex-col rounded-xl border border-border bg-card p-6 transition hover:border-primary/40" data-testid={`blog-card-${p.slug}`}>
              <span className="text-xs font-semibold text-primary">{p.category}</span>
              <h3 className="mt-2 font-display text-xl">{p.title}</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{p.excerpt}</p>
              <div className="mt-4 flex items-center gap-3 text-xs text-muted-foreground">
                <span>{new Date(p.date).toLocaleDateString()}</span>
                <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {p.read} min</span>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </div>
  );
}
